import { List } from "@mui/material";
import { useMemo } from "react";
import { Control, Controller, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { DateTimePickerElement } from "react-hook-form-mui/date-pickers";
import { useTranslation } from "react-i18next";

import { ObservationMediaDisplay } from "~/modules/mapEditor/components/ObservationMediaDisplay";
import FileUploaderStatus from "~/modules/media/components/FileUploaderStatus";
import FormTemplateDialogEditor from "~/shared/components/FormTemplate/FormTemplateDialogEditor";
import FormTemplateForm from "~/shared/components/FormTemplate/FormTemplateForm";
import { Shared_FormTemplateFragment } from "~/shared/components/FormTemplate/fragments/fragments.generated";
import SpeciesSelect from "~/shared/components/SpeciesSelect";

import {
    MapEditorObservationPanelObservationFragment,
    MapEditorObservationPanelResearchTypeOptionFragment,
} from "../fragments.generated";
import { ObservationUpdateFormValues } from "../useUpdateObservationMutation";
import { ObservationMediaPointCard } from "./ObservationMediaPointCard";
import { MapEditorObservationPanelObservationMediaPointFragment } from "./ObservationMediaPointCard/fragments.generated";
import { ObservationTypeSelect } from "./ObservationTypeSelect";

interface MapEditorObservationFormProps {
    control: Control<ObservationUpdateFormValues>;
    createHrefToObservationMediaPointCard: (element: MapEditorObservationPanelObservationMediaPointFragment) => string;
    locationFormTemplate: Shared_FormTemplateFragment;
    observation: MapEditorObservationPanelObservationFragment;
    researchTypes: MapEditorObservationPanelResearchTypeOptionFragment[];
    setValue: UseFormSetValue<ObservationUpdateFormValues>;
    watch: UseFormWatch<ObservationUpdateFormValues>;
}

export function MapEditorObservationForm({
    control,
    createHrefToObservationMediaPointCard,
    locationFormTemplate,
    observation,
    researchTypes,
    setValue,
    watch,
}: MapEditorObservationFormProps) {
    const { t } = useTranslation("mapEditor");

    const observationTypeId = watch("observationTypeId");
    const speciesId = watch("speciesId");
    const observationTypeOptions = useMemo(
        () => researchTypes.filter(rt => rt.speciesV2.some(s => s.species.id === speciesId)).flatMap(rt => rt.observationTypes),
        [researchTypes, speciesId],
    );
    const observationType = observationTypeOptions.find(x => x.id === observationTypeId) ?? observation.type;

    return (
        <>
            <Controller
                control={control}
                name="speciesId"
                render={({ field }) => (
                    <SpeciesSelect
                        disabled={field.disabled ?? false}
                        label={t("featureDetailObservation.species")}
                        value={field.value}
                        onChange={nextSpeciesId => {
                            if (nextSpeciesId !== field.value) {
                                setValue("observationTypeId", "");
                            }

                            field.onChange(nextSpeciesId);
                        }}
                    />
                )}
            />
            <Controller
                control={control}
                name="observationTypeId"
                rules={{ required: t("featureDetailObservation.observationTypeRequired") }}
                render={({ field, fieldState }) => (
                    <ObservationTypeSelect
                        disabled={field.disabled ?? false}
                        error={fieldState.error !== undefined}
                        helperText={fieldState.error?.message}
                        label={t("featureDetailObservation.observationType")}
                        onChange={field.onChange}
                        options={observationTypeOptions}
                        value={field.value}
                    />
                )}
            />
            <DateTimePickerElement control={control} label={t("featureDetailObservation.createdAt")} name="createdAt" />
            {observation.mediaPoints.length > 0 && (
                <List>
                    {observation.mediaPoints.map(element => (
                        <ObservationMediaPointCard
                            createHref={createHrefToObservationMediaPointCard}
                            key={element.id}
                            mediaPoint={element}
                        />
                    ))}
                </List>
            )}
            <Controller
                control={control}
                name="medias"
                render={({ field: { value, onChange } }) => (
                    <>
                        <FileUploaderStatus
                            disabled={false}
                            onUploadFinished={mediaItems => onChange([...mediaItems, ...value])}
                        />
                        <ObservationMediaDisplay
                            media={value}
                            onDelete={deletedMediaId => {
                                onChange(value.filter(media => media.id !== deletedMediaId));
                            }}
                        />
                    </>
                )}
            />
            <Controller
                control={control}
                name="preciseLocation"
                render={({ field }) => (
                    <FormTemplateDialogEditor
                        form={locationFormTemplate}
                        values={field.value}
                        onChange={values => field.onChange(values)}
                        disabled={false}
                    />
                )}
            />
            <Controller
                control={control}
                name="answers"
                render={({ field }) => (
                    <FormTemplateForm
                        form={observationType.formTemplate}
                        values={field.value}
                        onChange={values => field.onChange(values)}
                        disabled={false}
                    />
                )}
            />
        </>
    );
}
